/**
 * Public student application — multi-step admissions form.
 * Mirrors the admin Student Enrollment wizard but simplified per spec:
 *   1. Personal & contact details (SIN only if country=Canada)
 *   2. Program (category → published program → admission term → campus)
 *   3. Document upload (5 categories, PDF/PNG/JPG, 3 MB max)
 *   4. Review + submit (sends ZIP to admissions@hccbc.com)
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    Loader2, ChevronRight, ChevronLeft, Upload, Check, FileText, Trash2,
    GraduationCap, ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = "hccbc_application_v1";

const STEPS = [
    { key: "details", label: "Your details" },
    { key: "program", label: "Program" },
    { key: "documents", label: "Documents" },
    { key: "review", label: "Review & submit" },
];

const COUNTRIES = [
    "Canada", "India", "China", "Philippines", "Nigeria", "United States",
    "United Kingdom", "Pakistan", "Bangladesh", "Iran", "Vietnam", "Brazil",
    "Mexico", "South Korea", "Other",
];

function fmtError(e, fallback = "Something went wrong") {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
    return e?.message || fallback;
}

function Stepper({ active }) {
    return (
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="apply-stepper">
            {STEPS.map((s, idx) => {
                const isDone = idx < active;
                const isActive = idx === active;
                return (
                    <div key={s.key} className="flex items-center">
                        <div
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs ${
                                isActive
                                    ? "border-[#134B30] bg-[#134B30] text-white"
                                    : isDone
                                        ? "border-emerald-600 text-emerald-700 bg-emerald-50"
                                        : "border-[#E1DAC5] text-muted-foreground"
                            }`}
                            data-testid={`apply-step-${s.key}`}
                        >
                            <span className="font-mono">{idx + 1}</span>
                            <span>{s.label}</span>
                            {isDone && <Check className="h-3 w-3" />}
                        </div>
                        {idx < STEPS.length - 1 && (
                            <ChevronRight className="h-3 w-3 mx-1 text-muted-foreground" />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function Apply() {
    const [step, setStep] = useState(0);
    const [params] = useSearchParams();
    const [options, setOptions] = useState(null);
    const [programs, setPrograms] = useState([]);

    const [form, setForm] = useState({
        // Personal
        first_name: "", middle_name: "", last_name: "",
        email: "", phone: "", date_of_birth: "", gender: "", visa_status: "",
        // Address
        country: "", address_line1: "", address_line2: "",
        city: "", state_province: "", postal_code: "", sin: "",
        // Program
        program_category: "", program_id: "",
        admission_term: "", campus: "",
        // Consent
        consent: false,
    });
    const [appCtx, setAppCtx] = useState(null); // {application_id, application_token, student_id}
    const [documents, setDocuments] = useState([]); // uploaded docs from server
    const [busy, setBusy] = useState(false);

    const refreshApp = useCallback(async (ctx) => {
        if (!ctx?.application_id || !ctx?.application_token) return;
        try {
            const { data } = await axios.get(
                `${API}/applications/${ctx.application_id}`,
                { params: { token: ctx.application_token } },
            );
            setDocuments(data.documents || []);
            setForm((f) => ({
                ...f,
                first_name: data.first_name || f.first_name,
                middle_name: data.middle_name || f.middle_name,
                last_name: data.last_name || f.last_name,
                email: data.email || f.email,
                phone: data.phone || f.phone,
                date_of_birth: data.date_of_birth || f.date_of_birth,
                gender: data.gender || f.gender,
                visa_status: data.visa_status || f.visa_status,
                country: data.country || f.country,
                address_line1: data.address_line1 || f.address_line1,
                address_line2: data.address_line2 || f.address_line2,
                city: data.city || f.city,
                state_province: data.state_province || f.state_province,
                postal_code: data.postal_code || f.postal_code,
                sin: data.sin || f.sin,
                program_category: data.program_category || f.program_category,
                program_id: data.program_id || f.program_id,
                admission_term: data.admission_term || f.admission_term,
                campus: data.campus || f.campus,
                consent: true,
            }));
        } catch (_e) {
            localStorage.removeItem(STORAGE_KEY);
            setAppCtx(null);
        }
    }, []);

    const loadOptions = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/applications/lookups/options`);
            setOptions(data);
        } catch (_e) {
            toast.error("Could not load application options.");
        }
    }, []);

    const restoreDraft = useCallback(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
            if (saved?.application_id) {
                // Schedule the state mutations as a microtask so we satisfy
                // React 19's "no synchronous setState in useEffect body" rule.
                Promise.resolve().then(() => {
                    setAppCtx(saved);
                    refreshApp(saved);
                });
            }
        } catch (_e) { /* ignore */ }
        const pid = params.get("program");
        if (pid) {
            Promise.resolve().then(() => {
                setForm((f) => ({ ...f, program_id: pid }));
            });
        }
    }, [params, refreshApp]);

    useEffect(() => {
        loadOptions();
        restoreDraft();
    }, [loadOptions, restoreDraft]);

    const loadPrograms = useCallback(async () => {
        if (!form.program_category) {
            // Defer setState to microtask to satisfy React 19's strict rule.
            Promise.resolve().then(() => setPrograms([]));
            return;
        }
        try {
            const { data } = await axios.get(`${API}/programs`, {
                params: { is_published: true },
            });
            const cat = form.program_category;
            const all = data?.items || data || [];
            const filtered = all.filter((p) => {
                if (!p.is_published || p.is_archived || p.is_deleted) return false;
                const tag = (p.credential_type || p.category || "").toLowerCase();
                if (cat === "diploma") return tag.includes("diploma");
                if (cat === "certificate") return tag.includes("certif");
                return true;
            });
            setPrograms(filtered.length ? filtered : all.filter((p) => p.is_published && !p.is_archived));
        } catch (e) {
            toast.error(fmtError(e, "Could not load programs"));
        }
    }, [form.program_category]);

    useEffect(() => { loadPrograms(); }, [loadPrograms]);

    const isCanada = (form.country || "").toLowerCase() === "canada";
    const detailsValid = !!(form.first_name.trim() && form.last_name.trim()
        && form.email.trim() && form.country.trim() && form.gender.trim()
        && form.visa_status.trim() && form.date_of_birth.trim());
    const programValid = !!(form.program_category && form.program_id
        && form.admission_term && form.campus);
    const requiredDocs = (options?.doc_categories || []).filter((d) => d.required);
    const documentsValid = (() => {
        const have = new Set(documents.map((d) => d.category));
        return requiredDocs.every((d) => have.has(d.value)) && form.consent;
    })();

    const persistAppCtx = (ctx) => {
        setAppCtx(ctx);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    };

    const createDraft = async () => {
        setBusy(true);
        try {
            // Legal disclaimer acknowledgement happens at Step 2 (Documents) before
            // the user can advance to Review/Submit. We send consent=true here so
            // backend draft creation can proceed; the real consent gate is captured
            // in form.consent and re-checked client-side before submit().
            const payload = { ...form, consent: true };
            if (!isCanada) payload.sin = "";
            const { data } = await axios.post(`${API}/applications`, payload);
            const ctx = {
                application_id: data.application_id,
                application_token: data.application_token,
                student_id: data.student_id,
            };
            persistAppCtx(ctx);
            toast.success(`Application started — your student ID is ${data.student_id}.`);
            setStep(2);
        } catch (e) {
            toast.error(fmtError(e, "Could not start application"));
        } finally {
            setBusy(false);
        }
    };

    const goNext = async () => {
        if (step === 0) {
            if (!detailsValid) {
                toast.error("Please fill in your name, email, date of birth, gender, country, and visa status.");
                return;
            }
            setStep(1);
            return;
        }
        if (step === 1) {
            if (!programValid) {
                toast.error("Please select a category, program, admission term, and campus.");
                return;
            }
            // Create the application draft now so we have an ID for uploads
            if (!appCtx) {
                await createDraft();
            } else {
                setStep(2);
            }
            return;
        }
        if (step === 2) {
            const have = new Set(documents.map((d) => d.category));
            const docsComplete = requiredDocs.every((d) => have.has(d.value));
            if (!docsComplete) {
                toast.error("Please upload all required documents.");
                return;
            }
            if (!form.consent) {
                toast.error("Please accept the disclaimer to continue.");
                return;
            }
            setStep(3);
            return;
        }
    };
    const goBack = () => setStep((s) => Math.max(0, s - 1));

    const uploadDoc = async (category, file) => {
        if (!appCtx) {
            toast.error("Save your details first.");
            return;
        }
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!["pdf", "png", "jpg", "jpeg"].includes(ext)) {
            toast.error("Only PDF, PNG, or JPG files are accepted.");
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            toast.error("File too large — max 3 MB.");
            return;
        }
        const fd = new FormData();
        fd.append("token", appCtx.application_token);
        fd.append("category", category);
        fd.append("file", file);
        try {
            const { data } = await axios.post(
                `${API}/applications/${appCtx.application_id}/documents`,
                fd,
            );
            setDocuments((docs) => [...docs, data]);
            toast.success(`${data.category_label} uploaded.`);
        } catch (e) {
            toast.error(fmtError(e, "Upload failed"));
        }
    };

    const removeDoc = async (doc) => {
        if (!appCtx) return;
        try {
            await axios.delete(
                `${API}/applications/${appCtx.application_id}/documents/${doc.id}`,
                { params: { token: appCtx.application_token } },
            );
            setDocuments((docs) => docs.filter((d) => d.id !== doc.id));
            toast.success("Removed.");
        } catch (e) {
            toast.error(fmtError(e, "Delete failed"));
        }
    };

    const submit = async () => {
        if (!appCtx) return;
        if (!form.consent) {
            toast.error("Please accept the disclaimer before submitting.");
            setStep(2);
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/applications/${appCtx.application_id}/submit`,
                null,
                { params: { token: appCtx.application_token } },
            );
            toast.success("Application submitted! Our admissions team will be in touch shortly.");
            setStep(4);
            localStorage.removeItem(STORAGE_KEY);
            // keep appCtx so we can show the confirmation
            void data;
        } catch (e) {
            toast.error(fmtError(e, "Submission failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!options) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#134B30]" />
            </div>
        );
    }

    // ----- Confirmation screen -----
    if (step === 4) {
        return (
            <div className="min-h-[70vh] bg-[#FBF5E8] py-12 px-6">
                <div className="max-w-2xl mx-auto">
                    <Card className="border-[#134B30]/20" data-testid="apply-confirmation">
                        <CardContent className="p-10 text-center">
                            <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
                                <Check className="h-7 w-7" />
                            </div>
                            <h1 className="font-display text-3xl text-[#134B30]">Application received</h1>
                            <p className="text-sm text-muted-foreground mt-2">
                                Thank you for applying to Heritage Community College.
                            </p>
                            <div className="mt-6 inline-flex flex-col items-center gap-1">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C79520]">Your student ID</div>
                                <div className="font-mono text-xl text-[#134B30]" data-testid="apply-student-id">
                                    {appCtx?.student_id}
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-6">
                                Our admissions team will review your documents and reach out to you
                                via email at the address you provided.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[70vh] bg-[#FBF5E8] py-10 px-6" data-testid="apply-page">
            <div className="max-w-3xl mx-auto">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-1 flex items-center gap-1.5">
                    <GraduationCap className="h-3 w-3" />
                    Heritage Community College
                </div>
                <h1 className="font-display text-3xl text-[#134B30] mb-2">Apply for admission</h1>
                <p className="text-sm text-muted-foreground mb-6">
                    Complete the form below to submit your application. All fields marked with
                    <strong> *</strong> are required.
                </p>

                <Stepper active={step} />

                {/* STEP 0 — Personal & contact ------------------------------- */}
                {step === 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Your details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <Label>First name *</Label>
                                    <Input
                                        value={form.first_name}
                                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                        data-testid="apply-first-name"
                                    />
                                </div>
                                <div>
                                    <Label>Middle name</Label>
                                    <Input
                                        value={form.middle_name}
                                        onChange={(e) => setForm({ ...form, middle_name: e.target.value })}
                                        data-testid="apply-middle-name"
                                    />
                                </div>
                                <div>
                                    <Label>Last name *</Label>
                                    <Input
                                        value={form.last_name}
                                        onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                        data-testid="apply-last-name"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Email *</Label>
                                    <Input
                                        type="email" value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        data-testid="apply-email"
                                    />
                                </div>
                                <div>
                                    <Label>Phone</Label>
                                    <Input
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        data-testid="apply-phone"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Date of birth *</Label>
                                    <Input
                                        type="date" value={form.date_of_birth}
                                        onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                                        data-testid="apply-dob"
                                    />
                                </div>
                                <div>
                                    <Label>Gender *</Label>
                                    <Select
                                        value={form.gender}
                                        onValueChange={(v) => setForm({ ...form, gender: v })}
                                    >
                                        <SelectTrigger data-testid="apply-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Female">Female</SelectItem>
                                            <SelectItem value="Male">Male</SelectItem>
                                            <SelectItem value="Non-binary">Non-binary</SelectItem>
                                            <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Country *</Label>
                                    <Select
                                        value={form.country}
                                        onValueChange={(v) => setForm({ ...form, country: v })}
                                    >
                                        <SelectTrigger data-testid="apply-country"><SelectValue placeholder="Select…" /></SelectTrigger>
                                        <SelectContent>
                                            {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Visa / residency status *</Label>
                                    <Select
                                        value={form.visa_status}
                                        onValueChange={(v) => setForm({ ...form, visa_status: v })}
                                    >
                                        <SelectTrigger data-testid="apply-visa-status">
                                            <SelectValue placeholder="Select…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="citizen">Canadian citizen</SelectItem>
                                            <SelectItem value="permanent_resident">Permanent resident</SelectItem>
                                            <SelectItem value="study_permit">Study permit</SelectItem>
                                            <SelectItem value="work_permit">Work permit</SelectItem>
                                            <SelectItem value="other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {isCanada && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>SIN (Canada only)</Label>
                                        <Input
                                            value={form.sin}
                                            onChange={(e) => setForm({ ...form, sin: e.target.value })}
                                            placeholder="000-000-000"
                                            maxLength={11}
                                            data-testid="apply-sin"
                                        />
                                    </div>
                                </div>
                            )}
                            <div>
                                <Label>Address</Label>
                                <Input
                                    value={form.address_line1}
                                    onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                                    placeholder="Street and number"
                                    className="mb-2"
                                    data-testid="apply-addr1"
                                />
                                <Input
                                    value={form.address_line2}
                                    onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                                    placeholder="Apt, suite, unit (optional)"
                                    data-testid="apply-addr2"
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label>City</Label>
                                    <Input
                                        value={form.city}
                                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                                        data-testid="apply-city"
                                    />
                                </div>
                                <div>
                                    <Label>State / Province</Label>
                                    <Input
                                        value={form.state_province}
                                        onChange={(e) => setForm({ ...form, state_province: e.target.value })}
                                        data-testid="apply-state"
                                    />
                                </div>
                                <div>
                                    <Label>Postal code</Label>
                                    <Input
                                        value={form.postal_code}
                                        onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                                        data-testid="apply-postal"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* STEP 1 — Program ----------------------------------------- */}
                {step === 1 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Program selection</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Category *</Label>
                                    <Select
                                        value={form.program_category}
                                        onValueChange={(v) => setForm({ ...form, program_category: v, program_id: "" })}
                                    >
                                        <SelectTrigger data-testid="apply-category"><SelectValue placeholder="Select category…" /></SelectTrigger>
                                        <SelectContent>
                                            {options.categories.map((c) =>
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>,
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Program *</Label>
                                    <Select
                                        value={form.program_id}
                                        onValueChange={(v) => setForm({ ...form, program_id: v })}
                                        disabled={!form.program_category}
                                    >
                                        <SelectTrigger data-testid="apply-program"><SelectValue placeholder={form.program_category ? "Select program…" : "Choose a category first"} /></SelectTrigger>
                                        <SelectContent>
                                            {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                                            {programs.length === 0 && (
                                                <div className="px-2 py-1.5 text-xs text-muted-foreground">No active programs available</div>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Admission term *</Label>
                                    <Select
                                        value={form.admission_term}
                                        onValueChange={(v) => setForm({ ...form, admission_term: v })}
                                    >
                                        <SelectTrigger data-testid="apply-term"><SelectValue placeholder="Select term…" /></SelectTrigger>
                                        <SelectContent>
                                            {options.terms.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Campus *</Label>
                                    <Select
                                        value={form.campus}
                                        onValueChange={(v) => setForm({ ...form, campus: v })}
                                    >
                                        <SelectTrigger data-testid="apply-campus"><SelectValue placeholder="Select campus…" /></SelectTrigger>
                                        <SelectContent>
                                            {options.campuses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* STEP 2 — Documents --------------------------------------- */}
                {step === 2 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Document upload</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-xs text-muted-foreground">
                                Accepted formats: <strong>PDF, PNG, JPG/JPEG</strong>. Max <strong>3 MB</strong> per file.
                            </p>
                            {(options.doc_categories || []).map((cat) => {
                                const uploaded = documents.filter((d) => d.category === cat.value);
                                return (
                                    <div key={cat.value} className="border border-[#E1DAC5] rounded-md p-3 bg-white">
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <div className="text-sm font-medium">
                                                    {cat.label}
                                                    {cat.required && <span className="text-rose-600 ml-1">*</span>}
                                                </div>
                                                {cat.value === "post_secondary" && (
                                                <div className="text-[10px] text-muted-foreground">Diploma, degree, master&apos;s, or other credentials</div>
                                                )}
                                                {cat.value === "language_test" && (
                                                    <div className="text-[10px] text-muted-foreground">IELTS, TOEFL, Duolingo, CELPIP, or iTEP — within the last 2 years</div>
                                                )}
                                            </div>
                                            <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-[#134B30] text-[#134B30] hover:bg-[#134B30]/5 rounded-md cursor-pointer transition">
                                                <Upload className="h-3.5 w-3.5" />
                                                Upload file
                                                <input
                                                    type="file"
                                                    accept=".pdf,.png,.jpg,.jpeg"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const f = e.target.files?.[0];
                                                        if (f) uploadDoc(cat.value, f);
                                                        e.target.value = "";
                                                    }}
                                                    data-testid={`apply-upload-${cat.value}`}
                                                />
                                            </label>
                                        </div>
                                        {uploaded.length > 0 ? (
                                            <ul className="text-xs space-y-1">
                                                {uploaded.map((d) => (
                                                    <li key={d.id} className="flex items-center justify-between bg-[#FBF5E8] rounded px-2 py-1">
                                                        <span className="flex items-center gap-1.5 truncate">
                                                            <FileText className="h-3 w-3 text-[#134B30] flex-shrink-0" />
                                                            <span className="truncate">{d.filename}</span>
                                                            <span className="text-muted-foreground">({Math.round(d.size / 1024)} KB)</span>
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeDoc(d)}
                                                            className="text-rose-600 hover:text-rose-800 flex-shrink-0"
                                                            data-testid={`apply-remove-doc-${d.id}`}
                                                            aria-label="Remove"
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-[11px] text-muted-foreground italic">No files uploaded yet.</div>
                                        )}
                                    </div>
                                );
                            })}
                            <div className="flex items-start gap-2 mt-2 p-3 rounded-md bg-[#FBF5E8] border border-[#E1DAC5]">
                                <Checkbox
                                    id="apply-consent"
                                    checked={form.consent}
                                    onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
                                    data-testid="apply-consent"
                                />
                                <label htmlFor="apply-consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                                    I understand that language proficiency test results are only accepted if issued within the
                                    last 2 years, and that final admission is subject to verification and assessment of my academic
                                    and supporting documents by Heritage Community College.
                                </label>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* STEP 3 — Review ------------------------------------------ */}
                {step === 3 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Review your application</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="bg-[#FBF5E8] border border-[#E1DAC5] rounded-md p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C79520] mb-1">Student ID</div>
                                <div className="font-mono text-lg text-[#134B30]">{appCtx?.student_id}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div><strong>Name:</strong> {form.first_name} {form.middle_name} {form.last_name}</div>
                                <div><strong>Email:</strong> {form.email}</div>
                                <div><strong>Phone:</strong> {form.phone || "—"}</div>
                                <div><strong>Country:</strong> {form.country}</div>
                                <div className="col-span-2"><strong>Program:</strong> {programs.find((p) => p.id === form.program_id)?.title || form.program_id}</div>
                                <div><strong>Category:</strong> {form.program_category}</div>
                                <div><strong>Term:</strong> {form.admission_term}</div>
                                <div className="col-span-2"><strong>Campus:</strong> {form.campus}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium mb-2">Documents ({documents.length})</div>
                                <ul className="text-xs space-y-1">
                                    {documents.map((d) => (
                                        <li key={d.id} className="flex items-center gap-1.5">
                                            <FileText className="h-3 w-3 text-[#134B30]" />
                                            <Badge variant="outline" className="text-[10px]">{d.category_label}</Badge>
                                            <span>{d.filename}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="text-xs text-muted-foreground italic flex items-center gap-1.5 mt-2">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Your complete application will be securely sent to our admissions team.
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Footer nav --------------------------------------------- */}
                <div className="flex items-center justify-between mt-6">
                    <Button
                        variant="outline"
                        onClick={goBack}
                        disabled={step === 0 || busy}
                        data-testid="apply-back"
                    >
                        <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    {step < 3 ? (
                        <Button
                            onClick={goNext}
                            disabled={busy}
                            className="bg-[#134B30] hover:bg-[#0F3D26]"
                            data-testid="apply-next"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                            {step === 1 && !appCtx ? "Save & continue" : "Next"}
                            <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    ) : (
                        <Button
                            onClick={submit}
                            disabled={busy}
                            className="bg-[#C79520] hover:bg-[#B17E0F] text-[#0A2818] font-semibold"
                            data-testid="apply-submit"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                            Submit application
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
